/**
 * Hook pour la fonctionnalité de coaching côté coach (dashboard)
 *
 * Permet de:
 * - Gérer ses disponibilités
 * - Voir ses clients et leurs données partagées
 * - Gérer ses sessions
 * - Voir ses statistiques
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  CoachProfile,
  CoachAvailability,
  CoachingSession,
  CoachStats,
  RecurringAvailability,
  SpecificAvailability,
  SessionStatus,
  DayOfWeek,
  TimeSlot,
} from '../types/coaching.types';

// ============================================================================
// useCoachRole - Vérifier si l'utilisateur est un coach
// ============================================================================

export const useCoachRole = () => {
  const { user } = useAuth();
  const [isCoach, setIsCoach] = useState(false);
  const [coachProfile, setCoachProfile] = useState<CoachProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user) {
      setIsCoach(false);
      setCoachProfile(null);
      setLoading(false);
      return;
    }

    let userUnsubscribe: (() => void) | null = null;
    let coachUnsubscribe: (() => void) | null = null;

    const setupListeners = () => {
      try {
        setLoading(true);

        // Listen to user role changes
        const userRef = doc(db, 'users', user.uid);
        userUnsubscribe = onSnapshot(
          userRef,
          (userSnap) => {
            if (userSnap.exists() && userSnap.data().role === 'coach') {
              // Listen to coach profile changes
              const coachRef = doc(db, 'coaches', user.uid);
              coachUnsubscribe = onSnapshot(
                coachRef,
                async (coachSnap) => {
                  if (coachSnap.exists()) {
                    const coachData = {
                      id: coachSnap.id,
                      ...coachSnap.data(),
                    } as CoachProfile;

                    // Récupérer la photo et le nom du profil utilisateur depuis users/{uid}
                    try {
                      const userRef = doc(db, 'users', user.uid);
                      const userSnap = await getDoc(userRef);
                      if (userSnap.exists()) {
                        const userData = userSnap.data() as { photoURL?: string; displayName?: string };
                        // Utiliser la photo du profil utilisateur si disponible, sinon garder celle du coach
                        if (userData.photoURL) {
                          coachData.photoURL = userData.photoURL;
                        }
                        // Utiliser le nom du profil utilisateur si disponible, sinon garder celui du coach
                        if (userData.displayName) {
                          coachData.displayName = userData.displayName;
                        }
                      }
                    } catch (err) {
                      // Warning(`Could not fetch user data for coach ${user.uid}:`, err);
                      // Continuer sans les données utilisateur
                    }

                    setCoachProfile(coachData);
                    setIsCoach(true);
                  } else {
                    setCoachProfile(null);
                    setIsCoach(false);
                  }
                  setLoading(false);
                },
                (err) => {
                  // Error('Error listening to coach profile:', err);
                  setError(err as Error);
                  setLoading(false);
                }
              );
            } else {
              // Clean up coach listener if exists
              if (coachUnsubscribe) {
                coachUnsubscribe();
                coachUnsubscribe = null;
              }
              setCoachProfile(null);
              setIsCoach(false);
              setLoading(false);
            }
          },
          (err) => {
            // Error('Error listening to user role:', err);
            setError(err as Error);
            setLoading(false);
          }
        );
      } catch (err) {
        // Error('Error setting up listeners:', err);
        setError(err as Error);
        setLoading(false);
      }
    };

    setupListeners();

    // Cleanup
    return () => {
      if (userUnsubscribe) userUnsubscribe();
      if (coachUnsubscribe) coachUnsubscribe();
    };
  }, [user]);

  return { isCoach, coachProfile, loading, error };
};

// ============================================================================
// useMyClients - Clients du coach
// ============================================================================

interface ClientInfo {
  id: string;
  name: string;
  email: string;
  photoURL?: string;
  totalSessions: number;
  lastSessionAt?: Timestamp;
  upcomingSession?: CoachingSession;
}

export const useMyClients = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState<ClientInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    let unsubscribe: (() => void) | null = null;
    let unsubscribeFallback: (() => void) | null = null;

    const sessionsRef = collection(db, 'coachingSessions');

    // Helper pour convertir les dates Firestore
    const convertToDate = (dateValue: any): Date => {
      if (dateValue instanceof Date) return dateValue;
      if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue && typeof dateValue.toDate === 'function') {
        return dateValue.toDate();
      }
      if (typeof dateValue === 'string' || typeof dateValue === 'number') {
        return new Date(dateValue);
      }
      return new Date();
    };

    const processClients = (sessions: CoachingSession[]) => {
      // Trier par date décroissante (côté client)
      sessions.sort((a, b) => {
        const dateA = convertToDate(a.scheduledAt);
        const dateB = convertToDate(b.scheduledAt);
        return dateB.getTime() - dateA.getTime();
      });

      // Group by client
      const clientMap = new Map<string, ClientInfo>();
      const now = new Date();

      for (const session of sessions) {
        const existing = clientMap.get(session.userId);
        const sessionDate = convertToDate(session.scheduledAt);

        if (!existing) {
          const upcomingSession =
            session.status === 'confirmed' && sessionDate > now
              ? session
              : undefined;

          clientMap.set(session.userId, {
            id: session.userId,
            name: session.userName,
            email: session.userEmail,
            photoURL: session.userPhotoURL,
            totalSessions: 1,
            lastSessionAt: session.scheduledAt,
            upcomingSession,
          });
        } else {
          existing.totalSessions++;
          if (!existing.upcomingSession &&
              session.status === 'confirmed' &&
              sessionDate > now) {
            existing.upcomingSession = session;
          }
        }
      }

      setClients(Array.from(clientMap.values()));
      setError(null);
      setLoading(false);
    };

    // Essayer d'abord avec orderBy
    const tryQueryWithOrderBy = () => {
      const q = query(
        sessionsRef,
        where('coachId', '==', user.uid),
        orderBy('scheduledAt', 'desc')
      );

      return onSnapshot(
        q,
        (snapshot) => {
          const sessions = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          })) as CoachingSession[];
          processClients(sessions);
        },
        (err: any) => {
          // Si l'erreur est due à un index manquant, utiliser le fallback
          if (err?.code === 'failed-precondition' || err?.message?.includes('index') || err?.message?.includes('requires an index')) {
            if (process.env.NODE_ENV === 'development') {
              // Info('ℹ️ Index Firestore manquant pour clients, utilisation du fallback sans orderBy');
            }
            tryQueryWithoutOrderBy();
          } else {
            // Error('Error fetching clients:', err);
            setError(err);
            setLoading(false);
          }
        }
      );
    };

    const tryQueryWithoutOrderBy = () => {
      const qWithoutOrder = query(
        sessionsRef,
        where('coachId', '==', user.uid)
      );

      unsubscribeFallback = onSnapshot(
        qWithoutOrder,
        (snapshot) => {
          const sessions = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          })) as CoachingSession[];
          processClients(sessions);
        },
        (fallbackErr) => {
          // Error('Error in fallback query for clients:', fallbackErr);
          setError(fallbackErr as Error);
          setLoading(false);
        }
      );
    };

    unsubscribe = tryQueryWithOrderBy();

    return () => {
      if (unsubscribe) unsubscribe();
      if (unsubscribeFallback) unsubscribeFallback();
    };
  }, [user]);

  return { clients, loading, error };
};

// ============================================================================
// useClientDetails - Détails d'un client et ses données partagées
// ============================================================================

interface ClientDetails {
  id: string;
  name: string;
  email: string;
  photoURL?: string;
  sessions: CoachingSession[];
  sharedCVIds: string[];
  shareJobSearches: boolean;
  shareApplications: boolean;
}

export const useClientDetails = (clientId: string | undefined) => {
  const { user } = useAuth();
  const [client, setClient] = useState<ClientDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user || !clientId) {
      setLoading(false);
      return;
    }

    let unsubscribe: (() => void) | null = null;
    let unsubscribeFallback: (() => void) | null = null;

    const sessionsRef = collection(db, 'coachingSessions');

    // Helper pour convertir les dates Firestore
    const convertToDate = (dateValue: any): Date => {
      if (dateValue instanceof Date) return dateValue;
      if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue && typeof dateValue.toDate === 'function') {
        return dateValue.toDate();
      }
      if (typeof dateValue === 'string' || typeof dateValue === 'number') {
        return new Date(dateValue);
      }
      return new Date();
    };

    const processClientDetails = (sessions: CoachingSession[]) => {
      // Trier par date décroissante (côté client)
      sessions.sort((a, b) => {
        const dateA = convertToDate(a.scheduledAt);
        const dateB = convertToDate(b.scheduledAt);
        return dateB.getTime() - dateA.getTime();
      });

      if (sessions.length === 0) {
        setClient(null);
        setLoading(false);
        return;
      }

      // Aggregate shared data from all sessions
      const allSharedCVIds = new Set<string>();
      let shareJobSearches = false;
      let shareApplications = false;

      for (const session of sessions) {
        if (session.sharedData) {
          session.sharedData.cvDraftIds?.forEach(id => allSharedCVIds.add(id));
          if (session.sharedData.shareJobSearches) shareJobSearches = true;
          if (session.sharedData.shareApplications) shareApplications = true;
        }
      }

      const firstSession = sessions[0];
      setClient({
        id: clientId,
        name: firstSession.userName,
        email: firstSession.userEmail,
        photoURL: firstSession.userPhotoURL,
        sessions,
        sharedCVIds: Array.from(allSharedCVIds),
        shareJobSearches,
        shareApplications,
      });
      setError(null);
      setLoading(false);
    };

    // Essayer d'abord avec orderBy (nécessite un index composite)
    const tryQueryWithOrderBy = () => {
      const q = query(
        sessionsRef,
        where('coachId', '==', user.uid),
        where('userId', '==', clientId),
        orderBy('scheduledAt', 'desc')
      );

      return onSnapshot(
        q,
        (snapshot) => {
          const sessions = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          })) as CoachingSession[];
          processClientDetails(sessions);
        },
        (err: any) => {
          // Si l'erreur est due à un index manquant, utiliser le fallback
          if (err?.code === 'failed-precondition' || err?.message?.includes('index') || err?.message?.includes('requires an index')) {
            if (process.env.NODE_ENV === 'development') {
              // Info('ℹ️ Index Firestore manquant pour client details, utilisation du fallback sans orderBy');
            }
            tryQueryWithoutOrderBy();
          } else {
            // Error('Error fetching client details:', err);
            setError(err);
            setLoading(false);
          }
        }
      );
    };

    const tryQueryWithoutOrderBy = () => {
      const qWithoutOrder = query(
        sessionsRef,
        where('coachId', '==', user.uid),
        where('userId', '==', clientId)
      );

      unsubscribeFallback = onSnapshot(
        qWithoutOrder,
        (snapshot) => {
          const sessions = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          })) as CoachingSession[];
          processClientDetails(sessions);
        },
        (fallbackErr) => {
          // Error('Error in fallback query for client details:', fallbackErr);
          setError(fallbackErr as Error);
          setLoading(false);
        }
      );
    };

    unsubscribe = tryQueryWithOrderBy();

    return () => {
      if (unsubscribe) unsubscribe();
      if (unsubscribeFallback) unsubscribeFallback();
    };
  }, [user, clientId]);

  return { client, loading, error };
};

// ============================================================================
// useClientCV - CV d'un client (si partagé)
// ============================================================================

export const useClientCV = (clientId: string | undefined, cvDraftId: string | undefined) => {
  const { user } = useAuth();
  const [cvData, setCVData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user || !clientId || !cvDraftId) {
      setLoading(false);
      return;
    }

    const fetchCV = async () => {
      try {
        setLoading(true);

        // First verify the coach has access via a session
        const sessionsRef = collection(db, 'coachingSessions');
        const q = query(
          sessionsRef,
          where('coachId', '==', user.uid),
          where('userId', '==', clientId)
        );
        const sessionsSnap = await getDocs(q);

        let hasAccess = false;
        sessionsSnap.docs.forEach(doc => {
          const session = doc.data() as CoachingSession;
          if (session.sharedData?.cvDraftIds?.includes(cvDraftId)) {
            hasAccess = true;
          }
        });

        if (!hasAccess) {
          throw new Error('Accès non autorisé à ce CV');
        }

        // Fetch CV
        const cvRef = doc(db, 'users', clientId, 'cvBuilder', 'data', 'drafts', cvDraftId);
        const cvSnap = await getDoc(cvRef);

        if (cvSnap.exists()) {
          setCVData(cvSnap.data());
        } else {
          setCVData(null);
        }
      } catch (err) {
        // Error('Error fetching client CV:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchCV();
  }, [user, clientId, cvDraftId]);

  return { cvData, loading, error };
};

// ============================================================================
// useClientSearches - Historique de recherches d'un client (si partagé)
// ============================================================================

export const useClientSearches = (clientId: string | undefined) => {
  const { user } = useAuth();
  const [searches, setSearches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user || !clientId) {
      setLoading(false);
      return;
    }

    const fetchSearches = async () => {
      try {
        setLoading(true);

        // First verify the coach has access via a session
        const sessionsRef = collection(db, 'coachingSessions');
        const q = query(
          sessionsRef,
          where('coachId', '==', user.uid),
          where('userId', '==', clientId)
        );
        const sessionsSnap = await getDocs(q);

        let hasAccess = false;
        sessionsSnap.docs.forEach(doc => {
          const session = doc.data() as CoachingSession;
          if (session.sharedData?.shareJobSearches) {
            hasAccess = true;
          }
        });

        if (!hasAccess) {
          setSearches([]);
          setLoading(false);
          return;
        }

        // Fetch job searches
        const searchesRef = collection(db, 'users', clientId, 'jobSearches');
        const searchesSnap = await getDocs(searchesRef);

        const searchList = searchesSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        setSearches(searchList);
      } catch (err) {
        // Error('Error fetching client searches:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchSearches();
  }, [user, clientId]);

  return { searches, loading, error };
};

// ============================================================================
// useCoachSessions - Sessions du coach
// ============================================================================

export const useCoachSessions = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<CoachingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    let unsubscribe: (() => void) | null = null;
    let unsubscribeFallback: (() => void) | null = null;

    const sessionsRef = collection(db, 'coachingSessions');

    // Helper pour convertir les dates Firestore
    const convertToDate = (dateValue: any): Date => {
      if (dateValue instanceof Date) return dateValue;
      if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue && typeof dateValue.toDate === 'function') {
        return dateValue.toDate();
      }
      if (typeof dateValue === 'string' || typeof dateValue === 'number') {
        return new Date(dateValue);
      }
      return new Date();
    };

    const processSessions = (sessionList: CoachingSession[]) => {
      // Trier par date décroissante (côté client)
      sessionList.sort((a, b) => {
        const dateA = convertToDate(a.scheduledAt);
        const dateB = convertToDate(b.scheduledAt);
        return dateB.getTime() - dateA.getTime();
      });
      setSessions(sessionList);
      setError(null);
      setLoading(false);
    };

    // Essayer d'abord avec orderBy
    const tryQueryWithOrderBy = () => {
      const q = query(
        sessionsRef,
        where('coachId', '==', user.uid),
        orderBy('scheduledAt', 'desc')
      );

      return onSnapshot(
        q,
        (snapshot) => {
          const sessionList = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          })) as CoachingSession[];
          processSessions(sessionList);
        },
        (err: any) => {
          // Si l'erreur est due à un index manquant, utiliser le fallback
          if (err?.code === 'failed-precondition' || err?.message?.includes('index') || err?.message?.includes('requires an index')) {
            if (process.env.NODE_ENV === 'development') {
              // Info('ℹ️ Index Firestore manquant pour coach sessions, utilisation du fallback sans orderBy');
            }
            tryQueryWithoutOrderBy();
          } else {
            // Error('Error fetching coach sessions:', err);
            setError(err);
            setLoading(false);
          }
        }
      );
    };

    const tryQueryWithoutOrderBy = () => {
      const qWithoutOrder = query(
        sessionsRef,
        where('coachId', '==', user.uid)
      );

      unsubscribeFallback = onSnapshot(
        qWithoutOrder,
        (snapshot) => {
          const sessionList = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          })) as CoachingSession[];
          processSessions(sessionList);
        },
        (fallbackErr) => {
          // Error('Error in fallback query for coach sessions:', fallbackErr);
          setError(fallbackErr as Error);
          setLoading(false);
        }
      );
    };

    unsubscribe = tryQueryWithOrderBy();

    return () => {
      if (unsubscribe) unsubscribe();
      if (unsubscribeFallback) unsubscribeFallback();
    };
  }, [user]);

  // Helper pour convertir les dates Firestore (pour le filtrage)
  const convertToDate = (dateValue: any): Date => {
    if (dateValue instanceof Date) return dateValue;
    if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }
    if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      return new Date(dateValue);
    }
    return new Date();
  };

  // Categorize sessions
  const now = new Date();
  const pendingSessions = sessions.filter(s => s.status === 'pending');
  const upcomingSessions = sessions.filter(
    s => s.status === 'confirmed' && convertToDate(s.scheduledAt) > now
  );
  const pastSessions = sessions.filter(
    s => s.status === 'completed' || (s.status === 'confirmed' && convertToDate(s.scheduledAt) < now)
  );

  return { sessions, pendingSessions, upcomingSessions, pastSessions, loading, error };
};

// ============================================================================
// useManageSession - Gérer une session (confirmer, compléter, annuler)
// ============================================================================

export const useManageSession = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const confirmSession = useCallback(
    async (sessionId: string, meetingLink?: string): Promise<boolean> => {
      if (!user) return false;

      try {
        setLoading(true);
        const sessionRef = doc(db, 'coachingSessions', sessionId);
        await updateDoc(sessionRef, {
          status: 'confirmed' as SessionStatus,
          meetingLink,
          confirmedAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        return true;
      } catch (err) {
        // Error('Error confirming session:', err);
        setError(err as Error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const completeSession = useCallback(
    async (sessionId: string, notes?: string): Promise<boolean> => {
      if (!user) return false;

      try {
        setLoading(true);
        const sessionRef = doc(db, 'coachingSessions', sessionId);

        const updates: any = {
          status: 'completed' as SessionStatus,
          completedAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };

        if (notes) {
          updates['notes.coachNotes'] = notes;
        }

        await updateDoc(sessionRef, updates);
        return true;
      } catch (err) {
        // Error('Error completing session:', err);
        setError(err as Error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const cancelSession = useCallback(
    async (sessionId: string, reason: string): Promise<boolean> => {
      if (!user) return false;

      try {
        setLoading(true);
        const sessionRef = doc(db, 'coachingSessions', sessionId);
        await updateDoc(sessionRef, {
          status: 'cancelled' as SessionStatus,
          cancellation: {
            cancelledBy: 'coach',
            cancelledAt: Timestamp.now(),
            reason: 'coach_unavailable',
            reasonDetails: reason,
            refundIssued: false,
          },
          updatedAt: Timestamp.now(),
        });
        return true;
      } catch (err) {
        // Error('Error cancelling session:', err);
        setError(err as Error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const updateSessionNotes = useCallback(
    async (sessionId: string, notes: { coachNotes?: string; sharedNotes?: string; actionItems?: string[]; recommendations?: string[] }): Promise<boolean> => {
      if (!user) return false;

      try {
        setLoading(true);
        const sessionRef = doc(db, 'coachingSessions', sessionId);

        const updates: any = { updatedAt: Timestamp.now() };
        if (notes.coachNotes !== undefined) updates['notes.coachNotes'] = notes.coachNotes;
        if (notes.sharedNotes !== undefined) updates['notes.sharedNotes'] = notes.sharedNotes;
        if (notes.actionItems !== undefined) updates['notes.actionItems'] = notes.actionItems;
        if (notes.recommendations !== undefined) updates['notes.recommendations'] = notes.recommendations;

        await updateDoc(sessionRef, updates);
        return true;
      } catch (err) {
        // Error('Error updating session notes:', err);
        setError(err as Error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  return { confirmSession, completeSession, cancelSession, updateSessionNotes, loading, error };
};

// ============================================================================
// useManageAvailability - Gérer les disponibilités
// ============================================================================

export const useManageAvailability = () => {
  const { user } = useAuth();
  const [availability, setAvailability] = useState<CoachAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Fetch current availability
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const availabilityRef = doc(db, 'coachAvailability', user.uid);
    const unsubscribe = onSnapshot(
      availabilityRef,
      async (snapshot) => {
        if (snapshot.exists()) {
          setAvailability(snapshot.data() as CoachAvailability);
          setLoading(false);
        } else {
          // Le document n'existe pas - créer avec les valeurs par défaut
          const defaultAvailability: CoachAvailability = {
            coachId: user.uid,
            recurring: [],
            specific: [],
            timezone: 'Europe/Zurich',
            bufferBetweenSessions: 15,
            advanceBookingDays: 30,
            lastMinuteBookingHours: 24,
          };

          try {
            // Créer le document dans Firestore pour que les utilisateurs puissent le voir
            await setDoc(availabilityRef, defaultAvailability);
            // Log('✅ Document coachAvailability créé automatiquement pour le coach:', user.uid);
            setAvailability(defaultAvailability);
          } catch (createErr) {
            // Error('Erreur lors de la création du document coachAvailability:', createErr);
            // En cas d'erreur, utiliser les valeurs par défaut en mémoire
            setAvailability(defaultAvailability);
          }
          setLoading(false);
        }
      },
      (err) => {
        // Error('Error fetching availability:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const saveAvailability = useCallback(
    async (newAvailability: CoachAvailability): Promise<boolean> => {
      if (!user) return false;

      try {
        setSaving(true);
        const availabilityRef = doc(db, 'coachAvailability', user.uid);
        await setDoc(availabilityRef, newAvailability);
        return true;
      } catch (err) {
        // Error('Error saving availability:', err);
        setError(err as Error);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [user]
  );

  const addRecurringSlot = useCallback(
    async (dayOfWeek: DayOfWeek, slots: TimeSlot[]): Promise<boolean> => {
      if (!user || !availability) return false;

      const newRecurring: RecurringAvailability = {
        id: `${user.uid}-${dayOfWeek}`,
        coachId: user.uid,
        dayOfWeek,
        slots,
        isActive: true,
      };

      const existingIndex = availability.recurring.findIndex(r => r.dayOfWeek === dayOfWeek);
      const newRecurringList = [...availability.recurring];

      if (existingIndex >= 0) {
        newRecurringList[existingIndex] = newRecurring;
      } else {
        newRecurringList.push(newRecurring);
      }

      return saveAvailability({
        ...availability,
        recurring: newRecurringList,
      });
    },
    [user, availability, saveAvailability]
  );

  const removeRecurringSlot = useCallback(
    async (dayOfWeek: DayOfWeek): Promise<boolean> => {
      if (!user || !availability) return false;

      return saveAvailability({
        ...availability,
        recurring: availability.recurring.filter(r => r.dayOfWeek !== dayOfWeek),
      });
    },
    [user, availability, saveAvailability]
  );

  const addSpecificAvailability = useCallback(
    async (date: Date, slots: TimeSlot[], isBlocked: boolean = false): Promise<boolean> => {
      if (!user || !availability) return false;

      const newSpecific: SpecificAvailability = {
        id: `${user.uid}-${date.toISOString().split('T')[0]}`,
        coachId: user.uid,
        date: Timestamp.fromDate(date),
        slots,
        isOverride: true,
        isBlocked,
      };

      const existingIndex = availability.specific.findIndex(
        s => s.date.toDate().toDateString() === date.toDateString()
      );
      const newSpecificList = [...availability.specific];

      if (existingIndex >= 0) {
        newSpecificList[existingIndex] = newSpecific;
      } else {
        newSpecificList.push(newSpecific);
      }

      return saveAvailability({
        ...availability,
        specific: newSpecificList,
      });
    },
    [user, availability, saveAvailability]
  );

  const removeSpecificAvailability = useCallback(
    async (date: Date): Promise<boolean> => {
      if (!user || !availability) return false;

      return saveAvailability({
        ...availability,
        specific: availability.specific.filter(
          s => s.date.toDate().toDateString() !== date.toDateString()
        ),
      });
    },
    [user, availability, saveAvailability]
  );

  return {
    availability,
    loading,
    saving,
    error,
    saveAvailability,
    addRecurringSlot,
    removeRecurringSlot,
    addSpecificAvailability,
    removeSpecificAvailability,
  };
};

// ============================================================================
// useCoachStats - Statistiques du coach
// ============================================================================

export const useCoachStats = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<CoachStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchStats = async () => {
      try {
        setLoading(true);

        // Fetch all sessions
        const sessionsRef = collection(db, 'coachingSessions');
        const q = query(sessionsRef, where('coachId', '==', user.uid));
        const sessionsSnap = await getDocs(q);

        const sessions = sessionsSnap.docs.map(doc => doc.data() as CoachingSession);

        // Calculate stats
        const now = new Date();
        const totalSessions = sessions.length;
        const completedSessions = sessions.filter(s => s.status === 'completed').length;
        const upcomingSessions = sessions.filter(
          s => s.status === 'confirmed' && s.scheduledAt.toDate() > now
        ).length;
        const cancelledSessions = sessions.filter(s => s.status === 'cancelled').length;

        const totalRevenue = sessions
          .filter(s => s.status === 'completed' && s.isPaid)
          .reduce((sum, s) => sum + s.price, 0);

        // Get unique clients
        const uniqueClients = new Set(sessions.map(s => s.userId));
        const repeatClients = Array.from(uniqueClients).filter(
          clientId => sessions.filter(s => s.userId === clientId).length > 1
        ).length;

        // Fetch reviews
        const reviewsRef = collection(db, 'coachReviews');
        const reviewsQuery = query(reviewsRef, where('coachId', '==', user.uid));
        const reviewsSnap = await getDocs(reviewsQuery);
        const reviews = reviewsSnap.docs.map(doc => doc.data());

        const averageRating =
          reviews.length > 0
            ? reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length
            : 0;

        // Monthly stats (last 6 months)
        const monthlyStats = [];
        for (let i = 5; i >= 0; i--) {
          const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);

          const monthSessions = sessions.filter(s => {
            const date = s.scheduledAt.toDate();
            return date >= monthStart && date <= monthEnd;
          });

          const monthClients = new Set(
            monthSessions.filter(s => s.status === 'completed').map(s => s.userId)
          );

          monthlyStats.push({
            month: `${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, '0')}`,
            sessions: monthSessions.filter(s => s.status === 'completed').length,
            revenue: monthSessions
              .filter(s => s.status === 'completed' && s.isPaid)
              .reduce((sum, s) => sum + s.price, 0),
            newClients: monthClients.size,
          });
        }

        setStats({
          totalSessions,
          completedSessions,
          upcomingSessions,
          cancelledSessions,
          totalRevenue,
          averageRating: Math.round(averageRating * 10) / 10,
          totalReviews: reviews.length,
          repeatClients,
          monthlyStats,
        });
      } catch (err) {
        // Error('Error fetching coach stats:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user]);

  return { stats, loading, error };
};

// ============================================================================
// useUpdateCoachProfile - Mettre à jour le profil coach
// ============================================================================

export const useUpdateCoachProfile = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateProfile = useCallback(
    async (updates: Partial<CoachProfile>): Promise<boolean> => {
      if (!user) return false;

      try {
        setLoading(true);
        const coachRef = doc(db, 'coaches', user.uid);
        await updateDoc(coachRef, {
          ...updates,
          updatedAt: Timestamp.now(),
        });
        return true;
      } catch (err) {
        // Error('Error updating coach profile:', err);
        setError(err as Error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  return { updateProfile, loading, error };
};

export default useCoachRole;
